
import { useState } from 'react';
import { StateTaxResult } from '../detail';

interface StateFAQSectionProps {
  stateName: string;
  stateData: any;
  salary: number;
  taxResult: StateTaxResult;
}

const StateFAQSection = ({
  stateName,
  stateData,
  salary,
  taxResult,
}: StateFAQSectionProps) => {
  const [openIndex, setOpenIndex] = useState<number | null>(0);

  const faqs = [
    {
      question: `How much tax do I pay in ${stateName} on $${salary.toLocaleString()}?`,
      answer: `On a salary of $${salary.toLocaleString()} in ${stateName}, you would pay approximately $${Math.round(
        taxResult.totalTax
      ).toLocaleString()} in total taxes, which represents an effective tax rate of ${(
        taxResult.effectiveRate * 100
      ).toFixed(2)}%. This includes federal income tax, ${
        stateData.topRate !== '0%'
          ? `state income tax (${stateData.topRate} top rate), `
          : ''
      }Social Security, and Medicare.`,
    },
    {
      question: `Does ${stateName} have state income tax?`,
      answer:
        stateData.topRate === '0%'
          ? `No, ${stateName} does not have a state income tax. Residents only pay federal income tax, Social Security (6.2%), and Medicare (1.45%). This makes ${stateName} one of the most tax‑friendly states in the country for workers.`
          : `Yes, ${stateName} has a ${stateData.stateTaxType.toLowerCase()} income tax system with a top marginal rate of ${stateData.topRate}. ${
              stateData.stateTaxType === 'Graduated'
                ? "Different portions of your income are taxed at different rates, so you won't pay the top rate on all your income."
                : 'All taxable income is taxed at the same flat rate.'
            }`,
    },
    {
      question: `What is the take‑home pay in ${stateName}?`,
      answer: `For a gross salary of $${salary.toLocaleString()} in ${stateName}, your take‑home pay would be approximately $${Math.round(
        taxResult.netSalary
      ).toLocaleString()} annually, or about $${Math.round(
        taxResult.netSalary / 12
      ).toLocaleString()} per month. This accounts for all mandatory deductions including federal taxes and payroll taxes.`,
    },
    {
      question: `How does ${stateName} compare to other states for taxes?`,
      answer:
        stateData.topRate === '0%'
          ? `${stateName} is one of only 9 states with no state income tax, making it among the most tax‑friendly states in the US. Residents keep significantly more of their paycheck compared to high‑tax states like California (13.3%) or New York (10.9%).`
          : `${stateName}'s top marginal rate of ${stateData.topRate} is ${
              parseFloat(stateData.topRate) > 5 ? 'above' : 'below'
            } the national average. Compare this to no‑income‑tax states like Texas, Florida, and Nevada, or high‑tax states like California (13.3%) and New York (10.9%).`,
    },
    {
      question: 'How accurate is this salary calculator?',
      answer: `Our calculator uses the latest 2026 tax brackets and rates for both federal and ${stateName} state taxes. While we strive for accuracy, individual circumstances such as deductions, credits, local taxes, and filing status can affect your actual tax liability. This calculator provides estimates for planning purposes.`,
    },
  ];

  return (
    <section className="py-16 bg-gray-50">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="text-3xl font-bold text-gray-900 text-center mb-12">
          Frequently Asked Questions
        </h2>

        <div className="space-y-4">
          {faqs.map((faq, index) => (
            <div
              key={index}
              className="bg-white rounded-xl shadow-sm overflow-hidden border border-gray-200"
            >
              <button
                onClick={() =>
                  setOpenIndex(openIndex === index ? null : index)
                }
                className="w-full px-6 py-5 flex items-center justify-between text-left hover:bg-gray-50 transition-colors cursor-pointer"
              >
                <span className="text-lg font-semibold text-gray-900 pr-4">
                  {faq.question}
                </span>
                <i
                  className={`ri-arrow-${
                    openIndex === index ? 'up' : 'down'
                  }-s-line text-2xl text-gray-400 flex-shrink-0`}
                />
              </button>
              {openIndex === index && (
                <div className="px-6 pb-5 border-l-4 border-teal-500 bg-teal-50 animate-fadeIn">
                  <p className="text-gray-700 leading-relaxed">{faq.answer}</p>
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default StateFAQSection;
